import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {IonIcon, IonSpinner, useIonAlert} from '@ionic/react';
import {downloadOutline, trashBinSharp} from 'ionicons/icons';

import Panel from '../ui/Panel';
import {listJobs, deleteJob, signedDownload, QueryJob} from '../../services/bigQueryService';
import './FileDownload.css';

/** Table names are how the export API records a query; people read datasets. */
const DATASET_LABEL: Record<string, string> = {
  CRASHES_BATCH: 'Crashes',
  TRANSCORE_INCIDENT_BATCH: 'Incidents',
  DETECTOR_BATCH: 'Detector',
  WEATHER_BATCH: 'Weather',
  HERE: 'HERE probe',
  HERE_STATIC: 'HERE static',
};

/** Jobs run before the table was recorded still carry it inside their SQL. */
function datasetOf(job: QueryJob): string {
  const table = job.table ?? job.sql?.match(/FROM\s+`[^`]*\.([A-Za-z0-9_]+)`/)?.[1];
  if (!table) {
    return 'Export';
  }
  return DATASET_LABEL[table] ?? table;
}

function rangeOf(job: QueryJob): string {
  const {start, end} = job.time_range ?? {};
  if (!start || !end) {
    return '-';
  }
  return `${start.slice(0, 10)} to ${end.slice(0, 10)}`;
}

function requestedOf(job: QueryJob): string {
  return job.created_at ? new Date(job.created_at).toLocaleString() : '-';
}

function sizeOf(job: QueryJob): string {
  const bytes = job.file_size;
  if (!bytes) {
    return '';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return ` (${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]})`;
}

function fileOf(job: QueryJob): string | null {
  return job.final_csv_url ?? job.csv_urls?.[0] ?? null;
}

const JobHistory: React.FC = () => {
  const [jobs, setJobs] = useState<QueryJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [downloadingId, setDownloadingId] = useState('');
  const [confirmDelete] = useIonAlert();

  const refresh = useCallback(async () => {
    try {
      setJobs(await listJobs());
      setError('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // An export takes minutes, so a list opened while one is running would sit
  // stale. Poll only while something is unfinished.
  const pending = jobs.some(job => job.status === 'queued' || job.status === 'running');
  useEffect(() => {
    if (!pending) {
      return;
    }
    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [pending, refresh]);

  /** Links are signed on demand, so a stale one cannot hand out the data. */
  const download = async (job: QueryJob) => {
    const id = job.id ?? job.job_id ?? '';
    setDownloadingId(id);
    try {
      window.location.href = await signedDownload(id, 'final');
      setError('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloadingId('');
    }
  };

  const remove = (job: QueryJob) => {
    const id = job.id ?? job.job_id ?? '';
    confirmDelete({
      header: 'Delete this download?',
      message: 'The CSV is removed from cloud storage and the record of the query is deleted. This cannot be undone.',
      buttons: [
        'Cancel',
        {
          text: 'Delete',
          handler: () => {
            setBusyId(id);
            deleteJob(id)
              .then(() => setJobs(current => current.filter(j => (j.id ?? j.job_id) !== id)))
              .catch(err => setError(err.message))
              .finally(() => setBusyId(''));
          },
        },
      ],
    });
  };

  if (loading) {
    return (
      <Panel title="Your downloads">
        <div className="empty-msg"><IonSpinner /></div>
      </Panel>
    );
  }

  if (jobs.length === 0) {
    return (
      <Panel title="No downloads yet">
        <div className="empty-msg">
          {error || 'You have not submitted any requests to download data'}
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="Your downloads"
      subtitle={`${jobs.length} export${jobs.length === 1 ? '' : 's'}, newest first`}
      accent="var(--db-grey)"
      icon={<IonIcon icon={downloadOutline} />}
    >
      {error && <div className="empty-msg">{error}</div>}
      <table className="download-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Date range</th>
            <th>Requested</th>
            <th>File</th>
            <th className="download-table__actions">Delete</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(job => {
            const id = job.id ?? job.job_id ?? '';
            const url = fileOf(job);
            return (
              <tr key={id}>
                <td>{datasetOf(job)}</td>
                <td>{rangeOf(job)}</td>
                <td className="download-table__date">{requestedOf(job)}</td>
                <td>
                  {job.status === 'done' && url && !job.is_empty && (
                    <button
                      type="button"
                      className="download-link"
                      onClick={() => download(job)}
                      disabled={downloadingId === id}
                    >
                      <IonIcon icon={downloadOutline} aria-hidden="true" />
                      {downloadingId === id ? 'Preparing...' : `Download CSV${sizeOf(job)}`}
                    </button>
                  )}
                  {job.status === 'done' && job.is_empty && (
                    <span className="download-status-pill">No matching rows</span>
                  )}
                  {job.status === 'failed' && (
                    <span className="download-status-pill">{job.error || 'Failed'}</span>
                  )}
                  {(job.status === 'queued' || job.status === 'running') && (
                    <span className="download-status-pill download-status-pill--pending">
                      {job.progress_msg || 'Preparing'}
                    </span>
                  )}
                  {job.status === 'done' && !url && !job.is_empty && (
                    <span className="download-status-pill">No file</span>
                  )}
                </td>
                <td className="download-table__actions">
                  <button
                    type="button"
                    className="download-delete"
                    onClick={() => remove(job)}
                    disabled={busyId === id}
                    aria-label={`Delete the ${datasetOf(job)} export from ${requestedOf(job)}`}
                  >
                    <IonIcon icon={trashBinSharp} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
};

export default JobHistory;
